import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const IAPIntegrationGuide = () => {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const codeExamples = {
    initialization: `// Initialize the Solis Games SDK
await SolisGamesSDK.init({
  apiKey: 'your_api_key_here',
  gameId: 'your_game_id',
  features: ['iap', 'analytics'] // Enable IAP feature
});`,

    getProducts: `// Fetch available IAP products for your game
try {
  const products = await SolisGamesSDK.iap.getProducts();

  console.log('Available products:', products);
  // products = [
  //   {
  //     id: 'uuid-1234',
  //     product_identifier: '100_gems',
  //     display_name: '100 Gems Pack',
  //     description: 'Get 100 premium gems',
  //     price_usd: 4.99,
  //     currency: 'USD',
  //     is_consumable: true
  //   },
  //   ...
  // ]

  // Display products in your game UI
  products.forEach(product => {
    createProductButton(product);
  });

} catch (error) {
  console.error('Failed to load products:', error);
}`,

    purchase: `// Initiate purchase when user clicks buy button
async function buyProduct(productId) {
  try {
    // Show loading indicator
    showLoadingUI();

    const result = await SolisGamesSDK.iap.purchase(productId);

    // Purchase successful!
    console.log('Purchase completed:', result);
    // result = {
    //   success: true,
    //   purchaseId: 'purchase_uuid',
    //   productId: '100_gems',
    //   message: 'Purchase completed'
    // }

    // Grant the item to the player
    grantItemToPlayer(result.productId);

    // Show success message
    showSuccessUI('Purchase completed!');

  } catch (error) {
    console.error('Purchase failed:', error);
    showErrorUI('Purchase failed. Please try again.');
  }
}

// Example: Purchase button handler
document.getElementById('buy-gems-btn').addEventListener('click', () => {
  buyProduct('uuid-for-100-gems-product');
});`,

    getPurchases: `// Get user's purchase history (useful for restoring non-consumables)
try {
  const purchases = await SolisGamesSDK.iap.getPurchases();

  console.log('User purchase history:', purchases);
  // purchases = [
  //   {
  //     id: 'purchase_uuid',
  //     product_identifier: 'vip_pass',
  //     product_name: 'VIP Pass',
  //     amount: 9.99,
  //     currency: 'USD',
  //     status: 'completed',
  //     created_at: '2025-12-15T10:30:00Z'
  //   },
  //   ...
  // ]

  // Restore non-consumable purchases on game launch
  purchases.forEach(purchase => {
    if (purchase.status === 'completed') {
      restorePurchase(purchase.product_identifier);
    }
  });

} catch (error) {
  console.error('Failed to fetch purchase history:', error);
}`,

    completeExample: `// Complete IAP integration example

class GameIAPManager {
  constructor() {
    this.products = [];
    this.loading = false;
  }

  async initialize() {
    try {
      // Initialize SDK with IAP feature
      await SolisGamesSDK.init({
        apiKey: 'your_api_key',
        gameId: 'your_game_id',
        features: ['iap', 'analytics']
      });

      // Load products
      await this.loadProducts();

      // Restore previous purchases (for non-consumables)
      await this.restorePurchases();

      console.log('IAP Manager initialized successfully');
    } catch (error) {
      console.error('IAP initialization failed:', error);
    }
  }

  async loadProducts() {
    this.loading = true;
    try {
      this.products = await SolisGamesSDK.iap.getProducts();
      this.renderProductUI();
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      this.loading = false;
    }
  }

  async buyProduct(productId) {
    if (this.loading) return;

    this.loading = true;
    this.showLoadingUI();

    try {
      const result = await SolisGamesSDK.iap.purchase(productId);

      // Find product details
      const product = this.products.find(p => p.id === productId);

      // Grant the item
      this.grantPurchase(product.product_identifier);

      // Show success
      this.showSuccessUI(\`\${product.display_name} purchased!\`);

      // Track purchase for analytics
      console.log('Purchase successful:', result);

    } catch (error) {
      console.error('Purchase failed:', error);
      this.showErrorUI('Purchase failed. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  async restorePurchases() {
    try {
      const purchases = await SolisGamesSDK.iap.getPurchases();

      purchases.forEach(purchase => {
        if (purchase.status === 'completed') {
          // Restore non-consumable items
          const product = this.products.find(
            p => p.product_identifier === purchase.product_identifier
          );

          if (product && !product.is_consumable) {
            this.grantPurchase(purchase.product_identifier);
          }
        }
      });

      console.log('Purchases restored');
    } catch (error) {
      console.error('Failed to restore purchases:', error);
    }
  }

  grantPurchase(productIdentifier) {
    // Your game logic to grant the item
    switch(productIdentifier) {
      case '100_gems':
        this.addGems(100);
        break;
      case 'vip_pass':
        this.activateVIP();
        break;
      case 'remove_ads':
        this.disableAds();
        break;
    }
  }

  renderProductUI() {
    // Render your shop UI with products
    const shopContainer = document.getElementById('shop');
    shopContainer.innerHTML = '';

    this.products.forEach(product => {
      const button = document.createElement('button');
      button.className = 'product-button';
      button.innerHTML = \`
        <div class="product-name">\${product.display_name}</div>
        <div class="product-price">$\${product.price_usd}</div>
      \`;
      button.onclick = () => this.buyProduct(product.id);
      shopContainer.appendChild(button);
    });
  }

  showLoadingUI() {
    // Show loading overlay
    document.getElementById('loading-overlay').style.display = 'block';
  }

  showSuccessUI(message) {
    alert(message); // Replace with your success UI
    document.getElementById('loading-overlay').style.display = 'none';
  }

  showErrorUI(message) {
    alert(message); // Replace with your error UI
    document.getElementById('loading-overlay').style.display = 'none';
  }

  addGems(amount) {
    // Your game logic
    console.log(\`Added \${amount} gems\`);
  }

  activateVIP() {
    // Your game logic
    console.log('VIP activated');
  }

  disableAds() {
    // Your game logic
    console.log('Ads disabled');
  }
}

// Initialize on game load
const iapManager = new GameIAPManager();
iapManager.initialize();`,

    localTesting: `// Local testing (localhost environment)

// The SDK automatically detects localhost and provides demo products
// No real charges are made in local development

await SolisGamesSDK.init({
  apiKey: 'test_key', // Any key works in local mode
  gameId: 'test_game',
  features: ['iap']
});

const products = await SolisGamesSDK.iap.getProducts();
// Returns demo products:
// [
//   {
//     id: 'demo_product_1',
//     product_identifier: '100_gems',
//     display_name: '100 Gems Pack',
//     price_usd: 4.99,
//     ...
//   }
// ]

const result = await SolisGamesSDK.iap.purchase('demo_product_1');
// Simulates 2-second purchase delay
// Returns: { success: true, purchaseId: 'demo_purchase_123', ... }

console.log('✅ Local testing mode - no real charges');`,
  };

  const CodeBlock = ({ code, id, language = 'javascript' }) => (
    <div className="relative bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border">
        <span className="text-xs font-mono text-muted-foreground uppercase">{language}</span>
        <button
          onClick={() => copyCode(code, id)}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {copiedCode === id ? (
            <>
              <Icon name="Check" size={14} />
              Copied!
            </>
          ) : (
            <>
              <Icon name="Copy" size={14} />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm">
        <code className="font-mono text-foreground">{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>IAP Integration Guide - In-App Purchases | Solis Games</title>
        <meta name="description" content="Complete guide to integrating in-app purchases into your game using the Solis Games SDK. Variable pricing, automated payouts, 20% platform fee." />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-green-500/5 via-emerald-500/5 to-background py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <span className="mb-4 bg-green-500/10 text-green-600 border-green-500/20">
                DEVELOPER GUIDE
              </span>
              <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                In-App Purchase Integration
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Integrate variable-priced IAP into your game in minutes. Keep 80% of every purchase with automated payouts via Stripe Connect.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="Zap" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">5-Minute Setup</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="DollarSign" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">80% Revenue Share</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="Code" size={20} className="text-green-600" />
                  <span className="text-sm font-semibold">3 API Methods</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  Get Started Free
                  <Icon name="ArrowRight" size={20} />
                </Link>
                <a
                  href="#quick-start"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  Jump to Code
                  <Icon name="Code" size={20} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Prerequisites */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Prerequisites</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle2" size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Solis Games Account</h3>
                      <p className="text-sm text-muted-foreground">
                        Create a free account and register your game. Generate your API key from the dashboard.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle2" size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">SDK Integration</h3>
                      <p className="text-sm text-muted-foreground">
                        Include the Solis Games SDK script in your HTML. Works with any JavaScript game engine.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle2" size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">IAP Products Created</h3>
                      <p className="text-sm text-muted-foreground">
                        Configure your IAP products in the studio dashboard with pricing and descriptions.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="CheckCircle2" size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Payment Account Setup</h3>
                      <p className="text-sm text-muted-foreground">
                        Connect your bank account via Stripe Connect to receive automated payouts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Start */}
        <section id="quick-start" className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Quick Start</h2>
              <p className="text-muted-foreground mb-8">
                Follow these steps to integrate IAP into your game.
              </p>

              {/* Step 1: Initialize SDK */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <h3 className="text-2xl font-bold">Initialize the SDK with IAP</h3>
                </div>
                <p className="text-muted-foreground mb-4 ml-13">
                  Enable the <code className="px-2 py-1 bg-muted rounded text-sm font-mono">iap</code> feature when initializing the SDK.
                </p>
                <div className="ml-13">
                  <CodeBlock code={codeExamples.initialization} id="init" />
                </div>
              </div>

              {/* Step 2: Load Products */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                  <h3 className="text-2xl font-bold">Fetch Available Products</h3>
                </div>
                <p className="text-muted-foreground mb-4 ml-13">
                  Load the IAP products you configured in the studio dashboard.
                </p>
                <div className="ml-13">
                  <CodeBlock code={codeExamples.getProducts} id="products" />
                </div>
              </div>

              {/* Step 3: Purchase Flow */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                  <h3 className="text-2xl font-bold">Initiate Purchase</h3>
                </div>
                <p className="text-muted-foreground mb-4 ml-13">
                  When a player clicks the buy button, call <code className="px-2 py-1 bg-muted rounded text-sm font-mono">SolisGamesSDK.iap.purchase()</code>.
                  The platform handles the Stripe payment flow automatically.
                </p>
                <div className="ml-13">
                  <CodeBlock code={codeExamples.purchase} id="purchase" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Purchase Flow Works */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">How the Purchase Flow Works</h2>
              <p className="text-muted-foreground mb-8">
                Understanding what happens when a player makes a purchase.
              </p>

              <div className="space-y-4">
                {[
                  {
                    step: 1,
                    title: 'Player Clicks Buy',
                    desc: 'Your game calls SolisGamesSDK.iap.purchase(productId)',
                    icon: 'MousePointer',
                  },
                  {
                    step: 2,
                    title: 'Payment Modal Opens',
                    desc: 'Solis Games platform shows a Stripe payment modal with the product details and price',
                    icon: 'CreditCard',
                  },
                  {
                    step: 3,
                    title: 'Player Enters Payment',
                    desc: 'Player completes payment securely through Stripe (credit card, Google Pay, Apple Pay)',
                    icon: 'Lock',
                  },
                  {
                    step: 4,
                    title: 'Purchase Confirmed',
                    desc: 'The SDK returns success to your game with purchase details',
                    icon: 'CheckCircle2',
                  },
                  {
                    step: 5,
                    title: 'Grant Item',
                    desc: 'Your game grants the purchased item to the player (gems, VIP pass, etc.)',
                    icon: 'Gift',
                  },
                  {
                    step: 6,
                    title: 'Revenue Tracked',
                    desc: 'Platform automatically tracks revenue (80% to you, 20% platform fee) for monthly payout',
                    icon: 'TrendingUp',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-lg">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon name={item.icon} size={20} className="text-primary" />
                          <h3 className="font-semibold text-lg">{item.title}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-green-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-600 mb-2">Automatic Revenue Tracking</h4>
                    <p className="text-sm text-muted-foreground">
                      Every purchase is automatically tracked in your revenue dashboard with per-game breakdowns.
                      You'll receive automated monthly payouts via Stripe Connect with no manual work required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Purchase History */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Restoring Purchases</h2>
              <p className="text-muted-foreground mb-8">
                For non-consumable items (VIP passes, ad removal, etc.), restore previous purchases when the player logs in.
              </p>

              <CodeBlock code={codeExamples.getPurchases} id="restore" />

              <div className="mt-6 p-6 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-yellow-600 mb-2">Consumable vs Non-Consumable</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Configure this when creating products in the dashboard:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start gap-2">
                        <Icon name="ShoppingCart" size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Consumable:</strong> Can be purchased multiple times (gems, coins, lives) - do NOT restore</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Shield" size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Non-Consumable:</strong> One-time purchases (VIP pass, ad removal) - restore on game launch</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Complete Example */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Complete Implementation Example</h2>
              <p className="text-muted-foreground mb-8">
                A complete class-based IAP manager you can drop into your game.
              </p>

              <CodeBlock code={codeExamples.completeExample} id="complete" />

              <div className="mt-6 p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="Lightbulb" size={20} className="text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-600 mb-2">Usage Tips</h4>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>Initialize the IAP manager early in your game's startup sequence</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>Always show loading UI during purchase flow (can take 5-30 seconds)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>Handle errors gracefully - network issues, payment failures, user cancellations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>Track purchase analytics to understand what players are buying</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Local Testing */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Local Development & Testing</h2>
              <p className="text-muted-foreground mb-8">
                The SDK automatically detects localhost and provides demo products with simulated purchases - no real charges.
              </p>

              <CodeBlock code={codeExamples.localTesting} id="testing" />

              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-green-500/10 border-green-500/20">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle2" size={20} className="text-green-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-green-600 mb-2">Local Mode Features</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Returns demo products automatically</li>
                        <li>• Simulates 2-second purchase delay</li>
                        <li>• No real payments or API calls</li>
                        <li>• Test your UI and flow safely</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-blue-500/10 border-blue-500/20">
                  <div className="flex items-start gap-3">
                    <Icon name="Globe" size={20} className="text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-600 mb-2">Production Mode</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Loads real products from dashboard</li>
                        <li>• Uses Stripe test mode for testing</li>
                        <li>• Real payment flow (test cards)</li>
                        <li>• Full revenue tracking</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Best Practices</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="DollarSign" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Pricing Strategy</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Offer multiple price points ($0.99 to $99.99)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Create value bundles (more items = better deal)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Test different prices to find optimal conversion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Show "Best Value" badge on popular items</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="Sparkles" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">User Experience</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Always show loading state during purchase</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Provide clear success/error feedback</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Grant items immediately after purchase</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Restore purchases automatically on launch</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="Shield" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Security</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Never store prices in client-side code</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>All payment processing happens server-side</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Verify purchase status before granting items</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Use purchase history API for verification</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <Icon name="TrendingUp" size={20} className="text-primary mt-1" />
                    <h3 className="font-semibold text-lg">Analytics</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Track purchase funnel (views → clicks → conversions)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Monitor which products sell best</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>A/B test pricing and product descriptions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>Use Solis dashboard for revenue insights</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Troubleshooting</h2>

              <div className="space-y-6">
                <div className="p-6">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-red-500" />
                    "In-app purchases not enabled for this game"
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    <strong>Solution:</strong> Enable IAP feature when initializing SDK, and ensure IAP is enabled in your game settings.
                  </p>
                  <CodeBlock
                    code={`await SolisGamesSDK.init({
  apiKey: 'your_key',
  gameId: 'your_game',
  features: ['iap'] // ← Make sure this is included
});`}
                    id="trouble1"
                  />
                </div>

                <div className="p-6">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-red-500" />
                    "Failed to fetch IAP products"
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Possible causes:</strong>
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• No products created in the studio dashboard</li>
                    <li>• Products are not marked as "active"</li>
                    <li>• Network connection issues</li>
                    <li>• Invalid API key or game ID</li>
                  </ul>
                </div>

                <div className="p-6">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-red-500" />
                    "Purchase timeout"
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    <strong>Solution:</strong> The purchase flow timed out (5 minutes). This can happen if the player closes the payment modal or has network issues. Ask the player to try again.
                  </p>
                </div>

                <div className="p-6">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-red-500" />
                    Purchase completed but item not granted
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Solution:</strong> Check your <code className="px-2 py-1 bg-muted rounded font-mono">grantPurchase()</code> logic. The SDK returns success, but your game must handle granting the item. Use <code className="px-2 py-1 bg-muted rounded font-mono">getPurchases()</code> to verify the purchase exists.
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-card border border-border rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="HelpCircle" size={20} className="text-primary mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2">Still Need Help?</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Contact our developer support team or check the full API reference documentation.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="mailto:support@solisgames.com"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                      >
                        <Icon name="Mail" size={16} />
                        Email Support
                      </a>
                      <Link
                        to="/developers"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border text-foreground rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
                      >
                        <Icon name="BookOpen" size={16} />
                        View Docs
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-purple-500/10 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">
                Ready to Monetize Your Game?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Keep 80% of every purchase. Automated payouts. No platform fees.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity"
                >
                  Create Free Account
                  <Icon name="ArrowRight" size={20} />
                </Link>
                <Link
                  to="/developers/monetization"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-card border border-border text-foreground rounded-lg font-semibold text-lg hover:bg-muted transition-colors"
                >
                  View Revenue System
                  <Icon name="DollarSign" size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default IAPIntegrationGuide;
